import React, { useState } from "react";
import { Check, Copy } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Motion";

const TABS = [
  {
    id: "install",
    label: "server.cfg",
    code: `# 1. drop the resource folders into your resources directory
# 2. add these lines to server.cfg
ensure orvyn_fightstudio
ensure orvyn_karate_gis      # optional add-on

# standalone — no QBCore, Qbox, ESX, ox_lib or database.
# open the studio in-game with F7 or /fightstudio`,
  },
  {
    id: "config",
    label: "config.lua",
    code: `-- command and open key (F7 / /fightstudio, rebindable)
-- start countdown options and default
-- default playback speed
-- spectator limit (12 by default)
-- saved character limit
-- starter looks for the attacker and victim

-- content.lua toggles animation packs and gi presets
-- custom_animations.lua lists your own .ycd packs,
--   paired moves, solo moves and full fights

-- escrow-protected, but config, content and custom
-- animation files are left open for you to edit.`,
  },
  {
    id: "docs",
    label: "documentation",
    code: `README.md              # install and first run
user-guide.md          # every panel, step by step
animation-guide.md     # adding your own .ycd animations

# validate your custom animations in-game:
/fightstudio_check
# tests every custom animation and tells you
# exactly what's wrong.

Stuck on something that isn't covered?
Ask in the Orvyn Discord — we answer.`,
  },
];

export const Install = () => {
  const [tab, setTab] = useState(TABS[0].id);
  const [copied, setCopied] = useState(false);
  const active = TABS.find((t) => t.id === tab);

  const copy = () => {
    navigator.clipboard?.writeText(active.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <section id="install" className="relative border-b border-white/8 py-24 lg:py-36">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionLabel index="§ 06">Install</SectionLabel>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-display text-[clamp(1.9rem,4vw,3.1rem)] font-extrabold uppercase leading-[0.98] tracking-[-0.015em] text-white">
                One line.
                <br />
                <span className="text-white/35">Thirty seconds.</span>
              </h2>
              <p className="mt-6 text-sm leading-relaxed text-white/45">
                No framework bridge, no database, no dependency chain to untangle at 2am. Drop the
                folders in, ensure them, and open the studio with F7. Everything a player configures
                saves on their own computer.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  "No QBCore, Qbox, ESX, ox_lib or database",
                  "Escrow-protected, config and content left open",
                  "Add your own .ycd animations",
                  "README, user guide and animation guide included",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[13px] text-white/60">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--orv-blue))]" />
                    {f}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <Reveal delay={0.12} y={36}>
              <div className="overflow-hidden border border-white/12 bg-[#0b0b0e]">
                <div className="flex items-center justify-between border-b border-white/10 bg-[#101015] px-2 py-2">
                  <div className="flex">
                    {TABS.map((t) => (
                      <button
                        key={t.id}
                        data-testid={`install-tab-${t.id}`}
                        onClick={() => setTab(t.id)}
                        className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors duration-300 ${
                          tab === t.id ? "bg-white/8 text-white" : "text-white/35 hover:text-white/70"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={copy}
                    data-testid="install-snippet-copy"
                    className="mr-1 flex items-center gap-2 border border-white/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-white/55 transition-colors duration-300 hover:border-white/35 hover:text-white"
                  >
                    {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                <pre
                  className="overflow-x-auto p-6 font-mono text-[12px] leading-[1.9] text-white/65"
                  data-testid="install-snippet"
                >
                  <code>{active.code}</code>
                </pre>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
